import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useBridgeState } from '@digitaz/bridge-sdk-client';
import * as S from './RestoredAdDrawer.styles';
import { promotions } from '@/components/Ad/Promotions/Promotions.constants';
import { getLessPrice } from '@/components/Ad/Promotions/Promotions.helpers';
import { Promotion } from '@/components/Ad/Promotions/Promotion';
import { PromotionDrawer } from '@/components/Ad/Promotions/PromotionDrawer';
import { Drawer } from '@/components/Drawer/Drawer';
import { Button } from '@/components/UI/Button/Button';
import { useAdDetails } from '@/hooks/useAdDetails';
import { Typography } from '@/components/UI/Typography/Typography.styles';
import { AdStatus } from '@/types';
import { useToggleOpenState } from '@/hooks/useToggleOpenState';
import { AdKind } from '@/api/types/common.types';
import { AdPageTestIds } from '@/__e2e__/testIds/ad-page';
import { useServerDataContext } from '@/contexts';

interface RestoredAdDrawerProps {
  isOpen: boolean;
  toggle: () => void;
}

export const RestoredAdDrawer = ({ isOpen, toggle }: RestoredAdDrawerProps) => {
  const { t } = useTranslation();
  const { adDetails } = useAdDetails();
  const { initialAdDetails } = useServerDataContext();
  const adData = adDetails ?? initialAdDetails;
  const [isDrawerOpen, toggleDrawer] = useToggleOpenState();
  const [type, setType] = useState<null | AdKind>(null);
  const { isWebView } = useBridgeState();

  return (
    <Drawer
      dataTestId={AdPageTestIds.statusBanner.restoredAdDrawer}
      isHeadFixed={false}
      hasBottomMargin={false}
      isOpen={isOpen}
      close={toggle}
      title={t('pages.ad.ad_is_restored')}
      subtitleVariant="4"
      bottom={
        <Button
          appearance="secondary"
          isFullWidth
          onClick={toggle}
          data-testid={AdPageTestIds.statusBanner.restoredAdDrawerBtn}
        >
          {t('pages.ad.ad_is_restored_button')}
        </Button>
      }
      className="action"
    >
      <S.DrawerItem $isInTheMiddle>
        <S.RestoredAdDescription>
          <Typography.Body $variant="4">
            {t('pages.ad.ad_is_restored_subtitle')}
          </Typography.Body>
        </S.RestoredAdDescription>

        <Typography.Caption $variant="2">
          {t('pages.ad.ad_number')}
        </Typography.Caption>

        <S.AdNumber>{adData?.legacyResourceId}</S.AdNumber>
      </S.DrawerItem>

      <S.DrawerItem $isInTheMiddle>
        <S.PromotionTitle>
          <Typography.Title>
            {t('pages.ad.ad_is_restored_promotion')}
          </Typography.Title>
        </S.PromotionTitle>
        {adData &&
          promotions(
            t,
            getLessPrice(adData.bumpServices[0]),
            getLessPrice(adData.vipServices[0]),
            getLessPrice(adData.featuredServices[0]),
            adData.status === AdStatus.Published
          ).map(promotion => (
            <Promotion
              key={promotion.type}
              promotion={promotion}
              openDrawer={toggleDrawer}
              setType={setType}
            />
          ))}

        <PromotionDrawer
          isOpen={isDrawerOpen}
          toggle={toggleDrawer}
          onSuccessPayment={toggle}
          type={type}
          hideSuccessModal={!isWebView}
        />
      </S.DrawerItem>
    </Drawer>
  );
};
